// JAA-254 — pure helpers for the BuildingMap "fireworks" heart animation.
// Kept free of mapbox/DOM deps so the timing/threshold logic is isolated from
// the large BuildingMap effect. The mapbox glue (projection, marker spawn)
// lives in BuildingMap.tsx and feeds these functions plain numbers/features.

type Rng = () => number;

export interface HeartSpawn {
  buildingId: string;
  base: [number, number]; // building point [lng, lat]
  indexInCluster: number; // 0 = first/centered like of the building
  startDelayMs: number; // when to spawn, relative to burst start
  scale: number; // size variation, ~0.7–1.3
}

// Fireworks cadence, two tiers:
// - BUILDING_GAP_MS: pause between separate spots.
// - CLUSTER_GAP_MS: tighter spacing between the likes of one spot, so a cluster
//   pops as a quick group (but not instant) while still reading as one place.
const BUILDING_GAP_MS = 600;
const CLUSTER_GAP_MS = 300;
// Size variation (soap-bubble feel) — hearts aren't all the same size.
const SCALE_MIN = 0.7;
const SCALE_MAX = 1.3;

// Re-fire threshold: a move counts as a "new area" when the center shifts past
// half the viewport width, or the zoom changes by a full level.
const VIEWPORT_FRACTION = 0.5;
const ZOOM_STEP = 1;

export interface RefireInput {
  prevCenterPx: { x: number; y: number };
  nextCenterPx: { x: number; y: number };
  prevZoom: number;
  nextZoom: number;
  viewportWidthPx: number;
}

/** True when the viewport changed enough to justify re-playing the hearts. */
export function shouldRefire({
  prevCenterPx,
  nextCenterPx,
  prevZoom,
  nextZoom,
  viewportWidthPx,
}: RefireInput): boolean {
  if (Math.abs(nextZoom - prevZoom) >= ZOOM_STEP) return true;
  const dx = nextCenterPx.x - prevCenterPx.x;
  const dy = nextCenterPx.y - prevCenterPx.y;
  const movedPx = Math.sqrt(dx * dx + dy * dy);
  return movedPx > viewportWidthPx * VIEWPORT_FRACTION;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFeature = any;

/**
 * How many hearts one feature is worth. The route emits one feature per building
 * carrying its like tally, so a busy building is several hearts, not one.
 */
export function featureLikeCount(f: AnyFeature): number {
  return Math.max(1, Math.floor(Number(f?.properties?.like_count)) || 1);
}

/**
 * The longest prefix of `features` worth at most `budget` hearts, with the
 * feature that crosses the budget cut down rather than dropped. Counting in
 * likes rather than features is what makes a budget of N mean N hearts on
 * screen, which is what a person counts (JAA-377).
 */
export function trimToLikeBudget(
  features: AnyFeature[],
  budget: number,
): AnyFeature[] {
  const out: AnyFeature[] = [];
  let spent = 0;
  for (const f of features) {
    if (spent >= budget) break;
    const room = budget - spent;
    const count = featureLikeCount(f);
    out.push(
      count <= room
        ? f
        : { ...f, properties: { ...(f?.properties || {}), like_count: room } },
    );
    spent += Math.min(count, room);
  }
  return out;
}

/** Pull [lng,lat] + like_count + building id out of a /likes-in-bounds feature. */
function readFeature(
  f: AnyFeature,
): { buildingId: string; base: [number, number]; count: number } | null {
  const g = f?.geometry;
  if (g?.type !== 'Point' || !Array.isArray(g.coordinates)) return null;
  const base = g.coordinates as [number, number];
  const buildingId = String(f?.properties?.building_id ?? f?.id ?? '');
  if (!buildingId) return null;
  const count = featureLikeCount(f);
  return { buildingId, base, count };
}

function randomScale(rng: Rng): number {
  return SCALE_MIN + rng() * (SCALE_MAX - SCALE_MIN);
}

/**
 * Build the burst schedule. Randomize the order of *buildings* (not individual
 * hearts), then flatten so a building's likes stay consecutive — a cluster pops
 * as one quick local burst rather than scattering across the whole sweep.
 * Spacing is two-tier: BUILDING_GAP_MS between spots, the tighter CLUSTER_GAP_MS
 * between likes of one spot. Each heart gets a random `scale`. Deterministic
 * when `rng` is injected.
 */
export function buildFireworksSchedule(
  features: AnyFeature[],
  { rng = Math.random }: { rng?: Rng } = {},
): HeartSpawn[] {
  const buildings = features
    .map(readFeature)
    .filter(Boolean) as NonNullable<ReturnType<typeof readFeature>>[];

  // Fisher–Yates shuffle on buildings so areas don't always start same-first,
  // while each building's hearts remain grouped when flattened below.
  for (let i = buildings.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [buildings[i], buildings[j]] = [buildings[j], buildings[i]];
  }

  const spawns: HeartSpawn[] = [];
  let acc = 0;
  for (const b of buildings) {
    for (let k = 0; k < b.count; k++) {
      if (spawns.length > 0) {
        // New spot waits the building gap; extra likes of the same spot follow
        // at the tighter cluster gap so they pop as one group.
        acc += k === 0 ? BUILDING_GAP_MS : CLUSTER_GAP_MS;
      }
      spawns.push({
        buildingId: b.buildingId,
        base: b.base,
        indexInCluster: k,
        startDelayMs: acc,
        scale: randomScale(rng),
      });
    }
  }
  return spawns;
}

/**
 * Pick 1–2 random hearts from the in-view set for the idle trickle. Returned
 * spawns start immediately (startDelayMs 0); the caller spaces repeats via its
 * idle timer.
 */
export function pickSprinkle(
  features: AnyFeature[],
  { rng = Math.random, count = 1 }: { rng?: Rng; count?: number } = {},
): HeartSpawn[] {
  const parsed = features.map(readFeature).filter(Boolean) as {
    buildingId: string;
    base: [number, number];
    count: number;
  }[];
  if (parsed.length === 0) return [];
  const n = Math.min(count, parsed.length);
  const out: HeartSpawn[] = [];
  const used = new Set<number>();
  while (out.length < n) {
    const i = Math.floor(rng() * parsed.length);
    if (used.has(i)) continue;
    used.add(i);
    out.push({
      buildingId: parsed[i].buildingId,
      base: parsed[i].base,
      indexInCluster: 0,
      startDelayMs: 0,
      scale: randomScale(rng),
    });
  }
  return out;
}
